import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const fetchProducts = async () => {
  const { data } = await axios.get(`${API}/products`);
  return data.products;
};

export const fetchProduct = async (id) => {
  const { data } = await axios.get(`${API}/products/${id}`);
  return data;
};

export const createCheckout = async (items, originUrl, gift = {}) => {
  const { data } = await axios.post(`${API}/payments/checkout`, {
    items: items.map((i) => ({
      lookup_key: i.lookup_key,
      quantity: i.quantity,
      personalization: i.personalization || null,
    })),
    origin_url: originUrl,
    gift_wrap: !!gift.giftWrap,
    gift_message: gift.giftMessage || null,
    gift_recipient_email: gift.giftRecipientEmail || null,
    gift_recipient_name: gift.giftRecipientName || null,
  });
  return data;
};

export const lookupOrders = async (email) => {
  const { data } = await axios.get(`${API}/orders/lookup`, { params: { email } });
  return data.orders;
};

export const fetchFeed = async () => {
  const { data } = await axios.get(`${API}/feed`);
  return data;
};

export const fetchSettings = async () => {
  const { data } = await axios.get(`${API}/settings`);
  return data;
};

export const fetchCircles = async () => {
  const { data } = await axios.get(`${API}/circles`);
  return data.circles;
};

export const createCircle = async (payload) => {
  const { data } = await axios.post(`${API}/circles`, payload);
  return data;
};

export const fetchAcademyPlans = async () => {
  const { data } = await axios.get(`${API}/academy/plans`);
  return data.plans;
};

export const subscribeAcademy = async (plan, originUrl) => {
  const { data } = await axios.post(`${API}/academy/subscribe`, { plan, origin_url: originUrl });
  return data;
};

// Admin
export const adminVerify = async (token) => {
  const { data } = await axios.get(`${API}/admin/verify`, { headers: { "X-Admin-Token": token } });
  return data;
};

export const adminUpdateSettings = async (token, payload) => {
  const { data } = await axios.post(`${API}/admin/settings`, payload, { headers: { "X-Admin-Token": token } });
  return data;
};

export const adminListCircles = async (token) => {
  const { data } = await axios.get(`${API}/admin/circles`, { headers: { "X-Admin-Token": token } });
  return data.circles;
};

export const adminBroadcastLive = async (token, subject) => {
  const { data } = await axios.post(`${API}/admin/live/broadcast`, { subject: subject || null },
    { headers: { "X-Admin-Token": token } });
  return data;
};

export const getPaymentStatus = async (sessionId) => {
  const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
  return data;
};

export const subscribeNewsletter = async (email, name) => {
  const { data } = await axios.post(`${API}/newsletter`, { email, name });
  return data;
};

export const submitContact = async (payload) => {
  const { data } = await axios.post(`${API}/contact`, payload);
  return data;
};

// -------- PayPal --------
export const fetchPaypalConfig = async () => {
  const { data } = await axios.get(`${API}/paypal/config`);
  return data;
};

export const createPaypalOrder = async (items, originUrl, gift = {}, payerEmail) => {
  const { data } = await axios.post(`${API}/paypal/orders/create`, {
    items: items.map((i) => ({
      lookup_key: i.lookup_key,
      quantity: i.quantity,
      personalization: i.personalization || null,
    })),
    origin_url: originUrl,
    gift_wrap: !!gift.giftWrap,
    gift_message: gift.giftMessage || null,
    gift_recipient_email: gift.giftRecipientEmail || null,
    gift_recipient_name: gift.giftRecipientName || null,
    payer_email: payerEmail || null,
  });
  return data;
};

export const capturePaypalOrder = async (orderId) => {
  const { data } = await axios.post(`${API}/paypal/orders/${orderId}/capture`);
  return data;
};

// -------- Academy Gift --------
export const fetchGiftPlans = async () => {
  const { data } = await axios.get(`${API}/academy/gift/plans`);
  return data.plans;
};

export const createGiftCheckout = async (payload) => {
  const { data } = await axios.post(`${API}/academy/gift/checkout`, payload);
  return data;
};

export const fetchGift = async (code) => {
  const { data } = await axios.get(`${API}/academy/gift/${encodeURIComponent(code)}`);
  return data;
};

export const claimGift = async (code, payload) => {
  const { data } = await axios.post(`${API}/academy/gift/${encodeURIComponent(code)}/claim`, payload);
  return data;
};

// -------- Reader --------
export const fetchReaderToc = async () => {
  const { data } = await axios.get(`${API}/reader/toc`);
  return data.toc;
};

export const fetchReaderChapter = async (chapterId, token) => {
  const { data } = await axios.get(`${API}/reader/chapter/${chapterId}`,
    { params: token ? { token } : {} });
  return data;
};

export const requestReaderUnlock = async (email) => {
  const { data } = await axios.post(`${API}/reader/unlock/request`, { email });
  return data;
};

export const verifyReaderToken = async (token) => {
  const { data } = await axios.get(`${API}/reader/verify`, { params: { token } });
  return data;
};

export const fetchDictionary = async () => {
  const { data } = await axios.get(`${API}/reader/dictionary`);
  return data;
};

export const fetchQuiz = async (chapterId) => {
  const { data } = await axios.get(`${API}/reader/quiz/${chapterId}`);
  return data;
};

export const printableDownloadUrl = (key, sessionId) =>
  `${API}/download/printable/${key}?session_id=${encodeURIComponent(sessionId)}`;

// -------- AI (Claude Haiku) --------
export const streamReaderAsk = ({ chapterId, message, sessionId, onDelta, onDone, onError }) => {
  const controller = new AbortController();
  (async () => {
    try {
      const res = await fetch(`${API}/ai/reader/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ chapter_id: chapterId, message, session_id: sessionId }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) { onError?.(new Error(`HTTP ${res.status}`)); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          if (line === "[DONE]") { onDone?.(); return; }
          try {
            const obj = JSON.parse(line);
            if (obj.delta) onDelta?.(obj.delta);
            if (obj.error) onError?.(new Error(obj.error));
          } catch { /* skip */ }
        }
      }
      onDone?.();
    } catch (e) {
      if (e.name !== "AbortError") onError?.(e);
    }
  })();
  return () => controller.abort();
};

export const adminGenerateQuiz = async (token, chapterId) => {
  const { data } = await axios.post(`${API}/ai/quiz/generate`,
    { chapter_id: chapterId },
    { headers: { "X-Admin-Token": token }, timeout: 60000 });
  return data;
};

export const submitInquiry = async (payload) => {
  const { data } = await axios.post(`${API}/inquiry`, payload);
  return data;
};

export const fetchReviews = async () => {
  const { data } = await axios.get(`${API}/reviews`);
  return data.reviews;
};

export const createReview = async (payload) => {
  const { data } = await axios.post(`${API}/reviews`, payload);
  return data;
};
